// @file Define functions to make API calls
import getCsrfToken from '@@/bits/csrf_token'
import { captureFetchException } from '@@/bits/error_tracker'
import window, { browsingContextUid } from '@@/bits/global'
import { sleep } from '@@/bits/sleep'
import { getPathWithLeadingSlash } from '@@/bits/url'
import { viewAsPersonaForHeader } from '@@/surface/view_as_header_persona'
import { apiFetch } from '@padlet/arvo'
import type { FetchOptions } from '@padlet/fetch'
import { FetchError } from '@padlet/fetch'
import { FetchOptionMode } from '@padlet/fetch/src/types'

// Page request region for same-origin API calls.
type DataRegion = 'us' | 'eu'

interface RequestRegion {
  enabled: boolean
  region: DataRegion | null
}

let state: RequestRegion = { enabled: false, region: null }

function dataRegion(value: unknown): DataRegion | null {
  return value === 'us' || value === 'eu' ? value : null
}

function getRequestRegion(): RequestRegion {
  return { enabled: state.enabled, region: state.region }
}

function setPageRequestRegion(input: { objectRegionRouting: unknown; region: unknown }): void {
  const region = dataRegion(input.region)
  const enabled = input.objectRegionRouting === true && region != null
  state = { enabled, region: enabled ? region : null }
}

function adoptRedirectedRegion(region: DataRegion): void {
  if (!state.enabled) return
  state = { enabled: true, region }
}

// Empty when routing is off or the value is not us|eu, so callers spread it and send nothing extra.
function regionFetchOptions(region: unknown): { headers?: { 'X-Padlet-Region': DataRegion } } {
  const value = dataRegion(region)
  if (!state.enabled || value == null) return {}
  return { headers: { 'X-Padlet-Region': value } }
}

function pageRegionHeaders(): Record<string, string> {
  const { enabled, region } = getRequestRegion()
  if (!enabled || (region !== 'us' && region !== 'eu')) return {}
  return { 'X-Padlet-Region': region }
}

function redirectedRegion(error: unknown): 'us' | 'eu' | null {
  if (!(error instanceof FetchError) || error.status !== 412) return null
  const value = error.response?.headers?.get('X-Padlet-Region')
  return value === 'us' || value === 'eu' ? value : null
}

const getApiFetchCommonOptions = (): FetchOptions => {
  const viewAs = viewAsPersonaForHeader()

  return {
    headers: {
      'X-CSRF-Token': getCsrfToken(),
      'X-UID': window?.ww?.uid || browsingContextUid,
      ...pageRegionHeaders(),
      ...(viewAs && { 'X-View-As': viewAs }),
    },
  }
}

const apiFetchJson = async (url: string, options: FetchOptions = {}): Promise<any> => {
  const viewAs = viewAsPersonaForHeader()
  const callerRegion = options.headers?.['X-Padlet-Region']
  const alreadyFinal = options.headers?.['X-Padlet-Router'] === 'final'
  const noCors = options.mode === FetchOptionMode.noCors

  const commonOptions: FetchOptions = {
    headers: {
      Accept: 'application/json, application/vnd.api+json',
      'Content-Type': 'application/json; charset=utf-8',
      'X-CSRF-Token': getCsrfToken(),
      'X-UID': window?.ww?.uid || browsingContextUid,
      ...(noCors ? {} : pageRegionHeaders()),
      ...(viewAs && { 'X-View-As': viewAs }),
    },
  }

  // Merge `commonOptions.headers` with `options.headers` so callers don't have to provide
  // the common headers when they add custom headers.
  if (typeof options.headers === 'object') {
    options.headers = { ...commonOptions.headers, ...options.headers }
  }

  // `no-cors` (used so a request can reuse its preload) drops custom headers, so X-View-As never
  // arrives. Name the persona in the URL instead; Rails reads `?view_as=` first. A URL that already
  // names it, such as a preload href, is left alone so it still matches the preload.
  if (viewAs && options.mode === FetchOptionMode.noCors && !/[?&]view_as=/.test(url)) {
    options = { ...options, query: { ...options.query, view_as: viewAs } }
  }

  const pathWithLeadingSlash = getPathWithLeadingSlash(url)
  try {
    return await apiFetch<any>(pathWithLeadingSlash, commonOptions, options)
  } catch (error) {
    const redirected = redirectedRegion(error)
    if (noCors || alreadyFinal || redirected == null) throw error
    if (callerRegion == null) adoptRedirectedRegion(redirected)
    return await apiFetchJson(url, {
      ...options,
      headers: {
        ...options.headers,
        'X-Padlet-Region': redirected,
        'X-Padlet-Router': 'final',
      },
    })
  }
}

// status 0 = network failure (request never reached the server); status >= 500 = server error
const isRetryableError = (error: { status?: number }): boolean => {
  return error.status === 0 || (error.status != null && error.status >= 500)
}

// Retry gaps (not attempt counts). `sleep` picks a jittered delay in each [min, max] range.
const RETRY_DELAYS_IN_MS: Array<[number, number]> = [
  [300, 600],
  [1200, 2400],
  [3000, 6000],
]

const fetchJsonWithRetries = async (url: string, options: FetchOptions, source: string, maxTries = 4): Promise<any> => {
  let lastFailedError: FetchError | undefined

  for (let triesLeft = maxTries; triesLeft > 0; triesLeft -= 1) {
    try {
      const result = await apiFetchJson(url, options)
      if (lastFailedError) {
        captureFetchException(lastFailedError, { source, outcome: 'retry_succeeded' })
      }
      return result
    } catch (error) {
      lastFailedError = error
      if (triesLeft === 1 || !isRetryableError(error)) throw error
      const gapIndex = Math.min(maxTries - triesLeft, RETRY_DELAYS_IN_MS.length - 1)
      const [minDelay, maxDelay] = RETRY_DELAYS_IN_MS[gapIndex]
      await sleep(minDelay, maxDelay)
    }
  }
}

export { FetchError, apiFetchJson as fetchJson, fetchJsonWithRetries }
export { getApiFetchCommonOptions }
export { adoptRedirectedRegion, dataRegion, getRequestRegion, regionFetchOptions, setPageRequestRegion }
export type { DataRegion }
